<script setup lang="ts">
import { ArticleProps } from "./Article.props"
import { mapProducts, ProductAlgolia } from "~/utils/mapProducts"
import { WithContext, NewsArticle } from "schema-dts"
import { SeoBaseProps } from "components/seo/Base.props"
import { createFetchRequester } from "@algolia/requester-fetch"
import algoliasearch from "algoliasearch"
import { checkEnvSite } from "~/server/utils/getAlgoliaConfig"
import { productUtils } from "~/utils/product"

const { apiKey, applicationId } = useRuntimeConfig().public.algolia
const baseUrl = useRuntimeConfig().public.BASE_URL_APP
const route = useRoute()

const searchClient = algoliasearch(
  applicationId,
  process.server ? useRuntimeConfig().algolia.ssrApiKey : apiKey,
  {
    requester: createFetchRequester()
  }
)

const indexProducts = searchClient.initIndex(`TOP_PRODUCTS${checkEnvSite}`)

const props = defineProps<ArticleProps>()

const findItemsFromAlgolia = async (productCodes: string[]) => {
  const response = await indexProducts.getObjects(
    productCodes.map((code) => `product_${code}`)
  )

  const products = response?.results ?? []

  const itemsInStock =
    await productUtils.getItemsInStockOrRelatedItemsFromAlgolia(products)

  return mapProductsAlgolia(itemsInStock as ProductAlgolia[]).filter(
    (item) => item as ProductAlgolia
  )
}

const { data: productBlocksSuggestion } = useAsyncData(
  "findItemsFromAlgolia",
  async () =>
    await findItemsFromAlgolia(
      props.suggestionProducts.products.map((item) => `${item.productCode}`)
    ),
  {
    immediate: true,
    server: true,
    lazy: false
  }
)

const seoRef = computed((): SeoBaseProps => {
  return {
    ...props.seo,
    metaTitle: props.seo?.metaTitle || props.title
  }
})

const wordCounter = props.content
  ? useWordCounter(props.content, { isHtml: true })
  : {
      wordsCount: 0
    }

const jsonLd: ComputedRef<WithContext<NewsArticle>> = computed(() => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: props.title,
  image: [props.cover.image.image.url],
  datePublished: props.firstPublishingDate,
  dateModified: props.lastPublishingDate,
  ...(props.author
    ? {
        author: {
          "@type": "Person",
          name: props.author ?? ""
        }
      }
    : {}),
  publisher: {
    "@type": "Organization",
    url: "https://www.topfarmacia.it/",
    name: "Top Farmacia",
    logo: {
      "@type": "ImageObject",
      url: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
    }
  },
  articleSection:
    props.breadcrumbsLinks.links[props.breadcrumbsLinks.links.length - 1].label,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": route.fullPath
  },
  description: props.description ?? "",
  wordCount: wordCounter.wordsCount
}))

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(jsonLd.value))
    }
  ]
})
</script>

<template>
  <div class="article-wrapper grid-standard relative items-start">
    <SeoBase v-if="seo" v-bind="seoRef" />
    <UtilsBreadcrumbs
      v-bind="breadcrumbsLinks"
      class="negative-margined col-span-full row-start-1"
    />

    <CartUtilsLogin
      :upperText="$t('loginBanner.type2.upperText')"
      show-login-button
      class="negative-margined col-span-full row-start-2"
    />

    <ProductBlocksBrandsCategoryCover
      v-bind="cover"
      class="negative-margined col-span-full row-start-3"
    />

    <h1
      v-if="title"
      class="
        elephant-bold
        md:mammoth-bold
        col-span-full
        row-start-4
        my-6
        md:col-span-10 md:col-start-2
      "
    >
      {{ title }}
    </h1>

    <div
      id="textWrapper"
      class="
        wiki-article-text
        col-span-full
        row-start-5
        mb-6
        flex flex-col
        md:col-start-2
        md:mb-10
      "
      :class="
        suggestionProducts.products.length ? 'md:col-span-5' : 'md:col-span-10'
      "
      v-html="content"
    />

    <div
      class="
        col-span-full
        row-start-6
        flex flex-col
        gap-4
        md:col-span-10 md:col-start-2
        md:mb-8
        md:flex-row
        md:items-center
        md:justify-between
      "
    >
      <div class="flex flex-wrap items-center gap-4">
        <span class="mouse -mr-2" v-if="tags.length">
          {{ $t("article.categories") }}:
        </span>

        <BadgeTag v-for="tag in tags" v-bind="tag" />
      </div>

      <UtilsShare class="mb-2">
        <template #default>
          <UtilsButton
            :text="$t('article.share')"
            class="custom-btn-no-padding"
            theme="link-green"
          >
            <template #icon>
              <UtilsIcon name="Share.svg" color="green-main" class="h-5 w-5" />
            </template>
          </UtilsButton>
        </template>
      </UtilsShare>
    </div>
    <ProductBlocksSuggestion
      v-if="suggestionProducts.products.length"
      :products="productBlocksSuggestion"
      class="
        negative-margined
        col-span-full
        row-start-7
        md:col-span-4 md:col-start-8
        md:row-start-5
        md:m-0
      "
    />
    <!-- TODO: Aggiunta la sezione  -->
    <!-- <div v-if="suggestionCategories" class="col-span-full pb-8 pt-6">
      <p class="pig-bold md:hyppo-bold mb-4">
        {{ $t("article.moreUsedSubject") }}
      </p>

      <div class="flex gap-2">
        <UtilsButton
          v-for="(item, index) in suggestionCategories"
          :key="index"
          class="drop-shadow-01"
          theme="contained-white"
          v-bind="item"
        />
      </div>
    </div> -->

    <BannerNewsletter class="col-span-full row-start-[8]" />
    <div
      class="
        padded
        negative-margined
        col-span-full
        row-start-[9]
        bg-grey-main
        py-6
      "
      v-if="suggestionPosts.length"
    >
      <h3 class="hyppo-bold md:elephant-bold mb-2 md:mb-6">
        {{ $t("article.suggestion") }}
      </h3>

      <div class="flex flex-col justify-center gap-6 md:flex-row">
        <!-- For some reason, the CMS returns 'imageData' instead of image -->
        <CardBlogPost
          v-if="suggestionPosts"
          v-for="(post, index) in suggestionPosts"
          :key="index"
          :image="post.imageData"
          :title="post.title"
          :path="post.path"
          :tags="post.tags"
          :text="post.text"
          class="flex-1"
        />
      </div>
    </div>
  </div>
</template>
